import {
  FilterBuilder,
  useFilterContext,
} from "@/components/landing/FeeFilter";
import {
  FlightDetailTable,
  PricedFlightCardList,
} from "@/components/landing/PricedFlightTable";
import { usePage } from "@/components/page/PageContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Calendar } from "@/components/ui/calendar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAuthToken } from "@/lib/auth";
import { API_ENDPOINT } from "@/lib/constants";
import {
  getAvailableDates,
  getDailyReport,
  getPricedFlights,
} from "@/queries/landing";
import { useDateContext } from "@/stores/DateContext";
import { AlertCircle } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

function AvailableDaysCalendar({
  onDateSelected,
}: {
  onDateSelected: (date: string) => void;
}) {
  const { data: availableDates, isLoading } = getAvailableDates();
  const { singleDate } = useDateContext();

  // Create date at noon UTC to avoid any timezone issues
  function createUTCDate(dateString: string) {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  function handleDateSelect(date: Date | undefined) {
    if (date) {
      // Use local date components to preserve the user's selected date
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;
      onDateSelected(dateStr);
    }
  }

  if (isLoading) {
    return <Skeleton className="w-64 h-64" />;
  }

  const latestDate = availableDates?.length
    ? createUTCDate(availableDates[availableDates.length - 1])
    : new Date();

  return (
    <div>
      <Calendar
        mode="single"
        selected={singleDate ? createUTCDate(singleDate) : undefined}
        onSelect={handleDateSelect}
        defaultMonth={latestDate}
        className="rounded-md border"
        disabled={(date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const dateStr = `${year}-${month}-${day}`;
          return !availableDates?.includes(dateStr);
        }}
      />
    </div>
  );
}

const FEE_TYPE_NAMES = {
  "FeeType.LANDING": "Landing",
  "FeeType.PARKING": "Parking",
} as const;

const FEE_TIER_NAMES = {
  general_aviation: "General Aviation",
  signatory: "Signatory",
  non_signatory: "Non-Signatory",
} as const;

const FLIGHT_TYPE_NAMES = {
  Unscheduled: "Unscheduled",
  GA: "General Aviation",
  Scheduled: "Scheduled",
} as const;

function DownloadSection({ date, report }: { date: string; report: any }) {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const { appliedFilters } = useFilterContext();
  const navigate = useNavigate();

  // Handle download click for full flight data
  const handleDownloadFlights = async () => {
    try {
      setIsDownloading(true);

      const response = await fetch(
        `${API_ENDPOINT}/api/landing/report/day/${date}?export=flights`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            filter: appliedFilters,
          }),
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download flights");
      }

      const csvData = await response.text();

      // Create and download the file
      const blob = new Blob([csvData], {
        type: "text/csv;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `flights_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download flights:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="mt-6 border-t border-gray-600 pt-6">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => {
            navigate("/setup/airport?tab=exceptions");
          }}
          className="text-blue-400 hover:text-blue-300 hover:bg-slate-600 text-sm bg-slate-700 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Manage
        </button>
        <button
          onClick={handleDownloadFlights}
          disabled={isDownloading}
          className="text-blue-400 hover:text-blue-300 hover:bg-slate-600 text-sm bg-slate-700 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isDownloading ? (
            <>
              <span className="animate-spin">⏳</span>
              Downloading...
            </>
          ) : (
            "Download Flights CSV"
          )}
        </button>
      </div>
    </div>
  );
}

function SingleDayReport({ feeDate }: { feeDate: string }) {
  const { appliedFilters } = useFilterContext();
  const { data, isLoading } = getDailyReport({
    date: feeDate,
    enabled: true,
    filter: appliedFilters,
  });

  function formatDateToLocal(dateString: string) {
    // Create date at noon UTC to avoid any timezone issues
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC", // Ensure we use UTC for consistent display
    });
  }

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  };

  if (isLoading) {
    return <Skeleton className="w-full h-64" />;
  }

  if (!data) {
    return null;
  }

  // Sort carriers by amount and take top 3 instead of 5
  const topCarriers = [...data.carrier_breakdown]
    .sort((a, b) => b.amount_cents - a.amount_cents)
    .slice(0, 3);

  const formattedDate = formatDateToLocal(feeDate);

  return (
    <div className="border-gray-600 border bg-slate-900 rounded-lg shadow-lg p-6 w-full text-slate-200">
      <h2 className="text-xl font-semibold mb-4 text-slate-100">
        Landing Fee Report - {formattedDate}
      </h2>

      {/* KPI Section - Updated grid to make cards equal width */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-700 p-4 rounded-lg">
          <div className="text-sm text-slate-400">Total Revenue</div>
          <div className="text-2xl font-bold text-slate-100">
            {formatCurrency(data.total_fees)}
          </div>
        </div>
        <div className="bg-slate-700 p-4 rounded-lg">
          <div className="text-sm text-slate-400">Total Landings</div>
          <div className="text-2xl font-bold text-slate-100">
            {data.flight_count}
          </div>
        </div>
        <div className="bg-slate-700 p-4 rounded-lg">
          <div className="text-sm text-slate-400">Average Fee</div>
          <div className="text-2xl font-bold text-slate-100">
            {formatCurrency(data.total_fees / data.flight_count)}
          </div>
        </div>
      </div>

      {/* Breakdowns Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Flight Type Breakdown */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-2">
            Flight Types
          </h3>
          <div className="space-y-2">
            {data.flight_type_breakdown.map((breakdown) => (
              <div key={breakdown.item} className="flex justify-between">
                <span className="text-sm text-slate-300">
                  {FLIGHT_TYPE_NAMES[breakdown.item] || breakdown.item}
                </span>
                <span className="font-medium text-slate-200">
                  {formatCurrency(breakdown.amount_cents)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Carrier Breakdown */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-2">
            Top Carriers
          </h3>
          <div className="space-y-2">
            {topCarriers.map((breakdown) => (
              <div key={breakdown.item} className="flex justify-between">
                <span className="text-sm text-slate-300">{breakdown.item}</span>
                <span className="font-medium text-slate-200">
                  {formatCurrency(breakdown.amount_cents)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Fee Tier Breakdown */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-2">
            Fee Tiers
          </h3>
          <div className="space-y-2">
            {data.fee_tier_breakdown.map((breakdown) => (
              <div key={breakdown.item} className="flex justify-between">
                <span className="text-sm text-slate-300">
                  {FEE_TIER_NAMES[breakdown.item] || breakdown.item}
                </span>
                <span className="font-medium text-slate-200">
                  {formatCurrency(breakdown.amount_cents)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add DownloadSection after the breakdowns */}
      <DownloadSection date={feeDate} report={data} />
    </div>
  );
}

export function DayViewPage() {
  const navigate = useNavigate();
  const { date } = useParams();
  const { setTitles } = usePage();
  const { data: availableDates, isLoading: datesLoading } = getAvailableDates();
  const [selectedView, setSelectedView] = useState<"table" | "cards">("table");
  const { appliedFilters, resetFilters } = useFilterContext();
  const { singleDate, setSingleDate } = useDateContext();

  // Get current date from URL or latest available date
  const currentDate = useMemo(() => {
    if (datesLoading || !availableDates?.length) return "";
    return date && availableDates.includes(date)
      ? date
      : availableDates[availableDates.length - 1];
  }, [date, availableDates, datesLoading]);

  // Keep context in sync with URL and reset filters when URL date changes
  useEffect(() => {
    if (date && date !== singleDate) {
      setSingleDate(date);
      resetFilters();
    }
  }, [date, singleDate, setSingleDate, resetFilters]);

  // Update title to include the selected date
  const titles = useMemo(() => {
    const formattedDate = currentDate
      ? new Date(currentDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "";
    return [`Landing Fees`];
  }, [currentDate]);

  useEffect(() => {
    setTitles(titles);
  }, [setTitles, titles]);

  const {
    data: pricedFlightData,
    isLoading: flightsLoading,
    error: flightsError,
  } = getPricedFlights({
    enabled: !!currentDate,
    feeDate: currentDate,
    filter: appliedFilters,
  });

  if (datesLoading) {
    return <Skeleton className="w-full h-64" />;
  }

  if (!availableDates?.length) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>No available dates found</AlertDescription>
      </Alert>
    );
  }

  const handleDateSelected = (newDate: string) => {
    navigate(`/data/landing/day/${newDate}`);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row w-full p-4 gap-4">
        <div className="w-full md:w-auto shrink-0">
          <div className="uppercase mb-2 text-slate-400">Select Date:</div>
          <AvailableDaysCalendar onDateSelected={handleDateSelected} />
          <FilterBuilder />
        </div>
        <div className="flex-grow min-w-0">
          {currentDate && <SingleDayReport feeDate={currentDate} />}
          {flightsLoading && <Skeleton className="w-full h-64" />}
          {flightsError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>Failed to load flight data</AlertDescription>
            </Alert>
          )}
          {pricedFlightData && (
            <div className="w-full mt-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-slate-100">
                  Landings
                </h2>
                <Tabs
                  value={selectedView}
                  className="w-[200px]"
                  onValueChange={(value) =>
                    setSelectedView(value as "table" | "cards")
                  }
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="table">Table</TabsTrigger>
                    <TabsTrigger value="cards">Details</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="mt-4 overflow-x-auto">
                {selectedView === "table" ? (
                  <FlightDetailTable flights={pricedFlightData} />
                ) : (
                  <PricedFlightCardList flights={pricedFlightData} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
